import React, { useEffect, useRef } from 'react';
interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
}
interface Orbiter {
  radiusRatio: number;
  angle: number;
  speed: number;
  size: number;
}

/**
 * Layered navy backdrop built from four quiet systems:
 *  1. a dot matrix lit by a slow traveling wave (scale / reach)
 *  2. sweeping meridian arcs, slowly rotating (global footprint)
 *  3. gold points orbiting those arcs (movement without noise)
 *  4. a drifting node network (connection)
 * Everything is low-contrast and slow so it reads as depth, never decoration.
 */
export function AnimatedBackdrop({
  className = ''
}: {
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const spacing = 30;
    let frame = 0;
    let width = 0;
    let height = 0;
    let wave = -320;
    let rotation = 0;
    let nodes: Node[] = [];
    const orbiters: Orbiter[] = [{
      radiusRatio: 0.52,
      angle: 0.4,
      speed: 0.0022,
      size: 2.2
    }, {
      radiusRatio: 0.68,
      angle: 2.1,
      speed: -0.0016,
      size: 1.8
    }, {
      radiusRatio: 0.84,
      angle: 4.3,
      speed: 0.0012,
      size: 2.6
    }, {
      radiusRatio: 0.96,
      angle: 5.6,
      speed: -0.0009,
      size: 1.6
    }];
    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, width * ratio);
      canvas.height = Math.max(1, height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.min(38, Math.max(12, Math.round(width * height / 34000)));
      nodes = Array.from({
        length: count
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.14,
        vy: (Math.random() - 0.5) * 0.14
      }));
    };
    const draw = () => {
      context.clearRect(0, 0, width, height);
      const centerX = width * 0.76;
      const centerY = height * 0.5;
      const baseRadius = Math.max(width, height) * 0.46;

      // 1. Dot matrix, lit by the traveling wave.
      for (let x = spacing / 2; x < width; x += spacing) {
        for (let y = spacing / 2; y < height; y += spacing) {
          const distance = Math.abs(x - wave);
          const lit = Math.max(0, 1 - distance / 240);
          const alpha = 0.045 + lit * 0.34;
          context.fillStyle = lit > 0.35 ? `rgba(201, 169, 120, ${alpha})` : `rgba(226, 232, 240, ${alpha * 0.65})`;
          context.beginPath();
          context.arc(x, y, lit > 0.5 ? 1.5 : 1, 0, Math.PI * 2);
          context.fill();
        }
      }

      // 2. Meridian arcs.
      context.save();
      context.translate(centerX, centerY);
      context.rotate(rotation);
      for (let ring = 0; ring < 4; ring += 1) {
        const radius = baseRadius * (0.52 + ring * 0.16);
        context.strokeStyle = `rgba(176, 141, 87, ${0.2 - ring * 0.035})`;
        context.lineWidth = ring === 1 ? 1.2 : 0.8;
        context.beginPath();
        context.ellipse(0, 0, radius, radius * (0.34 + ring * 0.1), 0, 0, Math.PI * 2);
        context.stroke();
      }
      context.strokeStyle = 'rgba(226, 232, 240, 0.09)';
      context.lineWidth = 1;
      context.beginPath();
      context.arc(0, 0, baseRadius * 0.72, 0, Math.PI * 2);
      context.stroke();
      context.restore();

      // 3. Orbiting points with a soft halo.
      orbiters.forEach(orbiter => {
        const radius = baseRadius * orbiter.radiusRatio;
        const x = centerX + Math.cos(orbiter.angle) * radius;
        const y = centerY + Math.sin(orbiter.angle) * radius * 0.42;
        context.fillStyle = 'rgba(201, 169, 120, 0.1)';
        context.beginPath();
        context.arc(x, y, orbiter.size * 4, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = 'rgba(212, 180, 131, 0.85)';
        context.beginPath();
        context.arc(x, y, orbiter.size, 0, Math.PI * 2);
        context.fill();
        if (!reduceMotion) orbiter.angle += orbiter.speed;
      });

      // 4. Drifting node network.
      nodes.forEach(node => {
        if (!reduceMotion) {
          node.x += node.vx;
          node.y += node.vy;
        }
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.hypot(dx, dy);
          if (distance < 170) {
            context.strokeStyle = `rgba(176, 141, 87, ${0.16 * (1 - distance / 170)})`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(nodes[i].x, nodes[i].y);
            context.lineTo(nodes[j].x, nodes[j].y);
            context.stroke();
          }
        }
      }
      nodes.forEach(node => {
        context.fillStyle = 'rgba(226, 232, 240, 0.4)';
        context.beginPath();
        context.arc(node.x, node.y, 1.5, 0, Math.PI * 2);
        context.fill();
      });
      if (!reduceMotion) {
        wave += 1.05;
        if (wave > width + 320) wave = -320;
        rotation += 0.0004;
        frame = window.requestAnimationFrame(draw);
      }
    };
    resize();
    draw();
    const observer = new ResizeObserver(() => {
      resize();
      if (reduceMotion) draw();
    });
    observer.observe(canvas);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  return <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true" data-id="element-48" data-mp="22a3wdo637smx">
      <canvas ref={canvasRef} className="h-full w-full" data-id="element-49" data-mp="19nj8wysqg0on" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(19,28,43,0.92)_0%,rgba(19,28,43,0.55)_48%,rgba(19,28,43,0.2)_100%)]" aria-hidden="true" data-id="element-50" data-mp="19mzvnve120gy" />
    </div>;
}